/**
 * The minigames (owner, Sep 28: "build some minigames"; docs/DESIGN_ARCADE.md): the list, who may play what, and the world's
 * high-score boards. Pure functions — the Game (single player and the server) and the browser both use them.
 *
 * * **TV games** play on the game console at a TV (`game_console` in the pocket, F at the set): Avenue Racer, Kerb Hockey,
 *   Sprawl Stack, Cross the Avenue.
 * * **Phone games** play in the phone's Arcade app, each opened by an unlock the achievements grant (content/achievements: a
 *   `reward.unlock` of `game:<id>`): Siren Shooter, Delivery Dash, Gull Rush, Bag Beat. A sandbox world opens all of them.
 * * **Boards**: per world, per game, the ten best — one row per name, its best. A score is sent when a game ends and clamped
 *   here: a whole number, no more than the game's ceiling, no more than its best honest rate for the seconds played.
 */
import type { Achievement } from '../../content/types';
import type { AnyGame, Where } from './core';
import { racer } from './racer';
import { hockey } from './hockey';
import { stack } from './stack';
import { cross } from './cross';
import { shooter } from './shooter';
import { dash } from './dash';
import { gull } from './gull';
import { bag } from './bag';

export const GAMES: readonly AnyGame[] = [racer, hockey, stack, cross, shooter, dash, gull, bag];
export const TV_GAMES = GAMES.filter((g) => g.where === 'tv');
export const PHONE_GAMES = GAMES.filter((g) => g.where === 'phone');
export function gameById(id: string): AnyGame | undefined { return GAMES.find((g) => g.id === id); }
/** the item that turns a TV into a console */
export const CONSOLE_ITEM = 'game_console';
/** the TV's console on its menu (the `game` of a person's TvWatch when no game is picked yet) */
export const CONSOLE_MENU = 'menu';

export interface BoardRow { /** the player's name */ n: string; /** the score */ s: number }
export type Boards = Record<string, BoardRow[]>;
export const BOARD_SIZE = 10;
/** a game's seconds are counted at most this long (an hour at the TV is a long game already) */
export const MAX_SECS = 3600;
/** a score within its first seconds can still carry this much (a lucky start, a burst of combos) */
const SLACK = 500;

/** A sent score made honest enough: a whole number, 0 or more, under the game's ceiling and its rate × the seconds played. Unknown game, NaN: undefined. */
export function clampScore(g: AnyGame | undefined, score: unknown, secs: unknown): number | undefined {
  if (!g || typeof score !== 'number' || !Number.isFinite(score) || typeof secs !== 'number' || !Number.isFinite(secs)) return undefined;
  const t = Math.max(0, Math.min(MAX_SECS, secs));
  return Math.max(0, Math.min(Math.floor(score), g.maxScore, Math.ceil(g.maxRate * t + SLACK)));
}
/** A score onto a board: one row per name (its best), the ten best kept, ties to whoever got there first. The rank it took (1–10) or 0. */
export function boardPut(board: BoardRow[], name: string, score: number): { board: BoardRow[]; rank: number } {
  const n = name.slice(0, 24), mine = board.find((r) => r.n === n);
  if (mine && mine.s >= score) return { board, rank: 0 };
  const next = board.filter((r) => r.n !== n); let i = next.findIndex((r) => r.s < score); if (i < 0) i = next.length;
  if (i >= BOARD_SIZE) return { board, rank: 0 };
  next.splice(i, 0, { n, s: score });
  return { board: next.slice(0, BOARD_SIZE), rank: i + 1 };
}
/** a saved / received board book, cleaned: known games only, rows with a name and a score, ten a game, best first */
export function cleanBoards(raw: unknown): Boards {
  const out: Boards = {}; if (!raw || typeof raw !== 'object') return out;
  for (const g of GAMES) {
    const rows = (raw as Record<string, unknown>)[g.id]; if (!Array.isArray(rows)) continue;
    let b: BoardRow[] = [];
    for (const r of rows) if (r && typeof r.n === 'string' && typeof r.s === 'number' && Number.isFinite(r.s)) b = boardPut(b, r.n, Math.max(0, Math.min(g.maxScore, Math.floor(r.s)))).board;
    if (b.length) out[g.id] = b;
  }
  return out;
}

/** May this player play the game here? A TV game on the console, a phone game once unlocked (a sandbox opens everything). */
export function mayPlay(g: AnyGame, where: Where, has: (unlock: string) => boolean, sandbox: boolean): boolean {
  if (g.where !== where) return false;
  return !g.unlock || sandbox || has(g.unlock);
}
/** the part of an achievement (content/achievements, docs/DESIGN_ACHIEVEMENTS.md) the Arcade reads */
export type UnlockSource = Pick<Achievement, 'name' | 'description' | 'reward' | 'hidden' | 'off'>;
/** How a locked game opens: the world's achievement whose reward is its unlock (its name and what to do — a hidden one stays a
 *  mystery), else a word for any. */
export function unlockHow(achievements: readonly UnlockSource[], unlock: string): { name: string; text: string } {
  const a = achievements.find((x) => !x.off && x.reward?.unlock === unlock);
  if (a) return a.hidden ? { name: '???', text: 'A hidden achievement opens this one.' } : { name: a.name, text: a.description };
  return { name: 'Earn an achievement', text: 'Something you do in the city opens this one.' };
}
/** the Arcade's rows: every phone game, open or padlocked with how it opens */
export function arcadeRows(has: (unlock: string) => boolean, sandbox: boolean, achievements: readonly UnlockSource[]): { game: AnyGame; locked: boolean; how?: { name: string; text: string } }[] {
  return PHONE_GAMES.map((game) => { const locked = !mayPlay(game, 'phone', has, sandbox); return { game, locked, ...(locked && game.unlock ? { how: unlockHow(achievements, game.unlock) } : {}) }; });
}
